import { useEffect, useRef, useState, useCallback } from 'react'

interface WebSocketOptions {
  url: string
  onMessage?: (data: any) => void
  onOpen?: () => void
  onClose?: () => void
  onError?: (error: Event) => void
  reconnectAttempts?: number
  reconnectInterval?: number
  enabled?: boolean
}

export const useWebSocket = (options: WebSocketOptions) => {
  const {
    url,
    onMessage,
    onOpen,
    onClose,
    onError,
    reconnectAttempts = 3,
    reconnectInterval = 5000,
    enabled = true,
  } = options

  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectCountRef = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>()
  const isConnectingRef = useRef(false) // 添加连接状态标记

  // Store the latest callbacks in refs to avoid dependency issues
  const onMessageRef = useRef(onMessage)
  const onOpenRef = useRef(onOpen)
  const onCloseRef = useRef(onClose)
  const onErrorRef = useRef(onError)

  // store is documentHidden
  const [isDocumentHidden, setIsDocumentHidden] = useState(false)

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsDocumentHidden(document.hidden)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      disconnect()
    }
  }, [])

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage
    onOpenRef.current = onOpen
    onCloseRef.current = onClose
    onErrorRef.current = onError
  }, [onMessage, onOpen, onClose, onError])

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      console.log('WebSocket disconnected by client')
      wsRef.current.close(1000, 'Closed by client') // 1000 is normal closure
      wsRef.current = null
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = undefined
    }

    isConnectingRef.current = false
    setIsConnected(false)
  }, [])

  const connect = useCallback(() => {
    if (!enabled) return

    // 防止重复连接
    if (
      isConnectingRef.current ||
      (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      console.log('WebSocket already connecting, skipping...')
      return
    }

    // 如果已经连接，不重复连接
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected, skipping...')
      return
    }

    // Clear any existing connection
    if (wsRef.current) {
      wsRef.current.close()
    }

    isConnectingRef.current = true

    try {
      console.log('WebSocket connecting by Client to:', url)
      const ws = new WebSocket(url)

      ws.onopen = () => {
        console.log('WebSocket connected successfully')
        setIsConnected(true)
        reconnectCountRef.current = 0
        isConnectingRef.current = false
        onOpenRef.current?.()
      }

      ws.onmessage = (event) => {
        try {
          if (isDocumentHidden) return

          // Check if data is string (JSON) or other format
          if (typeof event.data === 'string') {
            const data = JSON.parse(event.data)
            onMessageRef.current?.(data)
          } else {
            // Handle other data types if needed
            // console.warn('Received non-JSON WebSocket message:', event.data)
            // onMessageRef.current?.(event.data)
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = (event) => {
        console.log(
          `WebSocket disconnected by Server. Clean: ${event.wasClean}, Code: ${event.code}, Reason: ${event.reason}, Reconnect count: ${reconnectCountRef.current}`,
        )
        setIsConnected(false)
        isConnectingRef.current = false
        onCloseRef.current?.()

        // 只有在非手动关闭且文档可见时才重连
        if (
          !isDocumentHidden &&
          !event.wasClean &&
          reconnectCountRef.current < reconnectAttempts &&
          enabled
        ) {
          console.log(
            `Attempting to reconnect (${reconnectCountRef.current + 1}/${reconnectAttempts}) in ${reconnectInterval}ms`,
          )
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectCountRef.current += 1
            connect()
          }, reconnectInterval)
        } else if (reconnectCountRef.current >= reconnectAttempts) {
          console.log('Max reconnection attempts reached, stopping reconnect')
        } else if (event.wasClean) {
          console.log('Connection closed cleanly, not reconnecting')
        } else if (isDocumentHidden) {
          console.log('Document hidden, not reconnecting')
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        isConnectingRef.current = false
        onErrorRef.current?.(error)
      }

      wsRef.current = ws
    } catch (error) {
      console.error('Failed to create WebSocket:', error)
      isConnectingRef.current = false
    }
  }, [url, enabled, reconnectAttempts, reconnectInterval]) // 移除isDocumentHidden依赖

  const sendMessage = useCallback((data: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data))
      return true
    }
    return false
  }, [])

  // 修复无限重连问题：只在初始化和URL/enabled变化时连接
  useEffect(() => {
    if (enabled) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [url, enabled])

  return {
    isConnected,
    sendMessage,
    disconnect,
    reconnect: connect,
  }
}
